import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import FiscalDeviceFormFields from 'src/features/systems/components/systemComponents/form/FiscalDeviceFormFields';
import GatewayDisplayFormFields from 'src/features/systems/components/systemComponents/form/GatewayDisplayFormFields';
import TicketPrinterFormFields from 'src/features/systems/components/systemComponents/form/TicketPrinterFormFields';
import {
    COMPONENT_DATA_TYPE_TRANSLATIONS_MAP,
    LISTEN_STATE_TRANSLATIONS_MAP,
    POSITION_TRANSLATIONS_MAP,
    SYSTEM_COMPONENT_TRANSLATIONS_MAP,
    SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP,
} from 'src/features/systems/utils/constants';
import { COMPONENTS_WITHOUT_ZONE, SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import { systemsMessages } from 'src/features/systems/utils/messages';
import {
    CAMERA_TYPE,
    COMMUNICATOR_TYPE,
    LISTEN_STATE,
    POSITION,
    SYSTEM_COMPONENT,
    SYSTEM_COMPONENT_TYPE,
} from 'src/features/systems/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

type SystemComponentFormProps = {
    form: UseFormReturn<SystemComponentFormSchemaValues>;
    onSubmit: (values: SystemComponentFormSchemaValues) => void;
    zoneOptions?: { value: string; label?: string }[];
    gatewayOptions?: { value: string; label?: string }[];
    logoTitle?: string;
};
function SystemComponentForm({ onSubmit, form, zoneOptions, gatewayOptions, logoTitle }: SystemComponentFormProps) {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { data: payStationsData } = useListPayStations({ parkingId });

    const { component } = form.watch();
    const isFiscalDevice = component === SYSTEM_COMPONENT.FISCAL_DEVICE;
    const isGatewayDisplay = component === SYSTEM_COMPONENT.GATEWAY_DISPLAY;
    const isCashOrCardApi = component === SYSTEM_COMPONENT.CASH_API || component === SYSTEM_COMPONENT.CARD_API;
    const isTicketPrinter = component === SYSTEM_COMPONENT.TICKET_PRINTER;
    const showsZone = !COMPONENTS_WITHOUT_ZONE.includes(component);

    const dataType = useWatch({ control: form.control, name: 'data.type' });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(systemsMessages.code)}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                options={Object.values(SYSTEM_COMPONENT_TYPE)}
                form={form}
                name="component_type"
                label={formatMessage(systemsMessages.componentType)}
                optionTranslationMap={SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                onChange={() => form.setValue('data.type', '')}
                options={Object.values(SYSTEM_COMPONENT)}
                form={form}
                name="component"
                label={formatMessage(systemsMessages.component)}
                optionTranslationMap={SYSTEM_COMPONENT_TRANSLATIONS_MAP}
            />
            {showsZone && (
                <FormField
                    control={form.control}
                    name="zone"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...systemsMessages.zone} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={`${formatMessage(systemsMessages.zone)}...`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {zoneOptions?.map((option) => (
                                                <SelectItem key={option?.value} value={option?.value}>
                                                    {option?.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            <FormField
                control={form.control}
                name="gateway"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage
                                id={systemsMessages.gateway.id}
                                defaultMessage={systemsMessages.gateway.defaultMessage}
                            />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(systemsMessages.gateway)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gatewayOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {component && component === SYSTEM_COMPONENT.GATEWAY_DISPLAY && <GatewayDisplayFormFields form={form} />}
            {component && isFiscalDevice && <FiscalDeviceFormFields form={form} />}
            {component && isTicketPrinter && <TicketPrinterFormFields form={form} logoTitle={logoTitle} />}

            {component && isCashOrCardApi && (
                <FormField
                    control={form.control}
                    name="pay_station"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...paymentsMessages.payStation} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={`${formatMessage(paymentsMessages.payStation)}...`}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {payStationsData?.results.map((option) => (
                                                <SelectItem key={option.id} value={option.id}>
                                                    {option.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}

            {component && !isGatewayDisplay && !isFiscalDevice && !isCashOrCardApi && !isTicketPrinter && (
                <>
                    <SelectFormField<SystemComponentFormSchemaValues>
                        options={Object.values(
                            component === SYSTEM_COMPONENT.LPR_CAMERA ? CAMERA_TYPE : COMMUNICATOR_TYPE,
                        )}
                        form={form}
                        name="data.type"
                        label={formatMessage(systemsMessages.type)}
                        optionTranslationMap={COMPONENT_DATA_TYPE_TRANSLATIONS_MAP}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.make"
                        label={formatMessage(systemsMessages.make)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.model"
                        label={formatMessage(systemsMessages.model)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.serial_number"
                        label={formatMessage(systemsMessages.serialNumber)}
                    />
                    {component === SYSTEM_COMPONENT.LPR_CAMERA ? (
                        <InputFormField<SystemComponentFormSchemaValues>
                            form={form}
                            name="data.rtsp_stream_url"
                            label={formatMessage(systemsMessages.RTSPStreamUrl)}
                        />
                    ) : (
                        <>
                            <InputFormField<SystemComponentFormSchemaValues>
                                form={form}
                                name="data.channel"
                                type="number"
                                label={formatMessage(systemsMessages.channel)}
                                min={0}
                            />
                            <InputFormField<SystemComponentFormSchemaValues>
                                form={form}
                                name="data.topic_code"
                                label={formatMessage(systemsMessages.topicCode)}
                            />
                            {component === SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR && (
                                <>
                                    <InputFormField<SystemComponentFormSchemaValues>
                                        form={form}
                                        name="data.persistent_channel"
                                        type="number"
                                        required={false}
                                        label={formatMessage(systemsMessages.persistentChannel)}
                                        min={0}
                                    />
                                    <InputFormField<SystemComponentFormSchemaValues>
                                        form={form}
                                        name="data.open_duration"
                                        type="number"
                                        label={formatMessage(systemsMessages.pulseDuration)}
                                        min={0}
                                    />
                                    {dataType === COMMUNICATOR_TYPE.SP_MQTT && (
                                        <>
                                            <InputFormField<SystemComponentFormSchemaValues>
                                                form={form}
                                                name="data.status_channel"
                                                type="number"
                                                required={false}
                                                label={formatMessage(systemsMessages.statusChannel)}
                                                min={0}
                                            />
                                            <SelectFormField<SystemComponentFormSchemaValues>
                                                options={Object.values(LISTEN_STATE)}
                                                form={form}
                                                name="data.status_listen_state"
                                                required={false}
                                                label={formatMessage(systemsMessages.statusListenState)}
                                                optionTranslationMap={LISTEN_STATE_TRANSLATIONS_MAP}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {component === SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR && (
                                <>
                                    <SelectFormField<SystemComponentFormSchemaValues>
                                        options={Object.values(POSITION)}
                                        form={form}
                                        name="data.position"
                                        label={formatMessage(systemsMessages.position)}
                                        optionTranslationMap={POSITION_TRANSLATIONS_MAP}
                                    />
                                    {dataType === COMMUNICATOR_TYPE.SP_MQTT && (
                                        <SelectFormField<SystemComponentFormSchemaValues>
                                            options={Object.values(LISTEN_STATE)}
                                            form={form}
                                            name="data.listen_state"
                                            label={formatMessage(systemsMessages.listenState)}
                                            optionTranslationMap={LISTEN_STATE_TRANSLATIONS_MAP}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Form>
    );
}

export default SystemComponentForm;
